// Cookie / tracking consent. Never throws — consent handling must not break the UI.
//
// public/index.html sets the starting state before any tag loads (Google
// Consent Mode defaults and the Meta pixel's consent flag) using the same
// storage key and region rule as this file. Keep the two in sync.
//
// Rule: visitors in the EEA, UK or Switzerland start with analytics and
// advertising OFF until they accept (opt-in). Everyone else starts with them
// ON and can turn them off (opt-out). Every visitor sees the banner once.

export const CONSENT_KEY = "aws_consent_v1";
export const CONSENT_VERSION = 1;
export const OPEN_SETTINGS_EVENT = "aws:open-cookie-settings";
export const CONSENT_CHANGED_EVENT = "aws:consent-changed";

// Europe/* covers the EEA, UK and Switzerland (and a few non-EEA countries,
// which is the safe direction to err). Atlantic zones add Iceland, the
// Canaries, Madeira, the Azores and the Faroes.
const OPT_IN_TZ = /^(Europe\/|Atlantic\/(Reykjavik|Canary|Madeira|Azores|Faroe)$)/;

export function requiresOptIn() {
  try {
    if (typeof window !== "undefined" && window.__awsConsent && typeof window.__awsConsent.optIn === "boolean") {
      return window.__awsConsent.optIn;
    }
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    return OPT_IN_TZ.test(tz);
  } catch (_) {
    return true; // can't tell: be conservative
  }
}

export function getStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== CONSENT_VERSION) return null;
    return { analytics: parsed.analytics === true, ads: parsed.ads === true, at: parsed.at };
  } catch (_) {
    return null;
  }
}

/** What is allowed right now: the stored choice, else the region default. */
export function currentConsent() {
  const stored = getStoredConsent();
  if (stored) return stored;
  const def = !requiresOptIn();
  return { analytics: def, ads: def, at: null };
}

export const hasAnalyticsConsent = () => currentConsent().analytics;
export const hasAdsConsent = () => currentConsent().ads;

const g = (on) => (on ? "granted" : "denied");

function applyToTags({ analytics, ads }) {
  try {
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        analytics_storage: g(analytics),
        ad_storage: g(ads),
        ad_user_data: g(ads),
        ad_personalization: g(ads),
      });
    }
  } catch (_) {}
  try {
    if (typeof window.fbq === "function") window.fbq("consent", ads ? "grant" : "revoke");
  } catch (_) {}
}

/** Save a choice and apply it to Google tags and the Meta pixel immediately. */
export function saveConsent({ analytics, ads }) {
  const choice = { v: CONSENT_VERSION, analytics: !!analytics, ads: !!ads, at: new Date().toISOString() };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(choice));
  } catch (_) {
    // storage blocked: the choice still applies for this page view
  }
  try {
    if (window.__awsConsent) window.__awsConsent.choice = choice;
  } catch (_) {}
  applyToTags(choice);
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: choice }));
  } catch (_) {}
  return choice;
}

/** Re-open the banner (footer "Cookie settings" link, privacy policy). */
export function openCookieSettings() {
  try {
    window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
  } catch (_) {}
}
